import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useGetCarInsuranceQuotesQuery } from 'services/api/clients.api';
import priceFormat from 'services/format/priceFormat';
import type { CarInsuranceQuote } from 'services/types/carInsuranceQuote';
import * as XLSX from 'xlsx';

interface CarInsuranceQuotesSectionProps {
  clientId: number;
}

/**
 * Car Insurance Quotes Section Component
 * Displays car insurance quotes grouped by company for a specific client
 */
const CarInsuranceQuotesSection: React.FC<CarInsuranceQuotesSectionProps> = ({ clientId }) => {
  const { data: quotesData, isLoading } = useGetCarInsuranceQuotesQuery(clientId);

  if (isLoading) {
    return (
      <Box>
        <Typography variant="body2">Cargando cotizaciones...</Typography>
      </Box>
    );
  }

  if (!quotesData?.quotes || Object.keys(quotesData.quotes).length === 0) {
    return null;
  }

  const paymentMethodLabels: Record<string, string> = {
    UPFRONT: 'Contado',
    PAC: 'PAC',
    PAT: 'PAT',
    INSTALLMENTS: 'Cuotas',
  };

  const frequencyLabels: Record<string, string> = {
    MONTHLY: 'Mensual',
    ANNUAL: 'Anual',
    TOTAL: 'Total',
  };

  const planTypeLabels: Record<string, string> = {
    SMALL: 'Pequeño',
    MEDIUM: 'Mediano',
    LARGE: 'Grande',
  };

  /**
   * Helper function to parse expiresAt date
   */
  const parseExpiresAtDate = (expiresAt: Date | string | null): Date | null => {
    if (!expiresAt) return null;
    if (typeof expiresAt === 'string') return new Date(expiresAt);
    return expiresAt;
  };

  /**
   * Helper function to format quote data for Excel
   */
  const formatQuoteForExcel = (quote: CarInsuranceQuote, index: number) => {
    const expiresAtDate = parseExpiresAtDate(quote.expiresAt);

    // Format premium values
    const premiumCLPStr = quote.premiumCLP ? `$${priceFormat.format(quote.premiumCLP)}` : '';
    const premiumUFStr = quote.premiumUF ? `UF ${quote.premiumUF.toFixed(2)}` : '';
    const primaValue = [premiumCLPStr, premiumUFStr].filter(Boolean).join(' / ') || 'N/A';

    return {
      Número: index + 1,
      Plan: quote.planName,
      Deducible: `UF ${quote.deductible}`,
      Prima: primaValue,
      'Método de Pago': `${paymentMethodLabels[quote.paymentMethod] || quote.paymentMethod} - ${
        frequencyLabels[quote.paymentFrequency] || quote.paymentFrequency
      }`,
      'Tipo de Plan': planTypeLabels[quote.planType] || quote.planType,
      'Válido hasta': expiresAtDate
        ? expiresAtDate.toLocaleDateString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        : '',
      'Valor UF': quote.ufValueAtDate ? `$${priceFormat.format(quote.ufValueAtDate)}` : '',
      'Tasa de Interés':
        quote.interestRate !== null && quote.interestRate !== undefined
          ? `${(quote.interestRate * 100).toFixed(2)}%`
          : '',
      Comisión:
        quote.commission !== null && quote.commission !== undefined ? `${(quote.commission * 100).toFixed(2)}%` : '',
      Descuento: quote.discount !== null && quote.discount !== undefined ? `${(quote.discount * 100).toFixed(2)}%` : '',
    };
  };

  /**
   * Download all quotes grouped by company in a single Excel file
   */
  const handleDownloadAllExcel = () => {
    if (!quotesData?.quotes) return;

    const workbook = XLSX.utils.book_new();

    Object.entries(quotesData.quotes).forEach(([company, clientsQuotes]) => {
      const clientQuotes: CarInsuranceQuote[] | undefined = clientsQuotes[clientId];
      if (!clientQuotes || clientQuotes.length === 0) return;

      const excelData = clientQuotes.map((quote, index) => formatQuoteForExcel(quote, index));
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const columnWidths = [
        { wch: 8 },
        { wch: 25 },
        { wch: 15 },
        { wch: 15 },
        { wch: 25 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
      ];
      worksheet['!cols'] = columnWidths;

      const sheetName = company.length > 31 ? company.substring(0, 31) : company;
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cotizaciones_seguro_auto_cliente_${clientId}_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  /**
   * Download quotes for a specific company
   */
  const handleDownloadCompanyExcel = (company: string, clientQuotes: CarInsuranceQuote[]) => {
    const workbook = XLSX.utils.book_new();
    const excelData = clientQuotes.map((quote, index) => formatQuoteForExcel(quote, index));
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const columnWidths = [
      { wch: 8 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
    ];
    worksheet['!cols'] = columnWidths;

    const sheetName = company.length > 31 ? company.substring(0, 31) : company;
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const sanitizedCompanyName = company.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
    link.download = `cotizaciones_${sanitizedCompanyName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          mb: 1,
          color: 'primary.main',
        }}
      >
        📝 Cotizaciones
      </Typography>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadAllExcel}
          disabled={!quotesData || Object.keys(quotesData.quotes).length === 0}
        >
          Descargar todas las cotizaciones (Excel)
        </Button>
      </Box>
      {Object.entries(quotesData.quotes).map(([company, clientsQuotes]) => {
        const clientQuotes: CarInsuranceQuote[] | undefined = clientsQuotes[clientId];
        if (!clientQuotes || clientQuotes.length === 0) {
          return null;
        }

        return (
          <Box key={company} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                }}
              >
                🏢 {company}
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadCompanyExcel(company, clientQuotes)}
              >
                Descargar {company}
              </Button>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CarInsuranceQuotesSection;
